import {
  AccountCategory,
  AccountCategoryCode,
  AccountCreateRequest,
  AccountState,
  LicensePackage,
  LicensePackageCode,
  Permission,
} from '@account-users/account/models';
import * as fromAccount from '@account-users/account/state';
import * as fromLicenses from '@account-users/account/state';
import { PrepareCreateAccount } from '@account-users/account/state/accounts/accounts.actions';
import { State } from '@account-users/account/state/accounts/accounts.reducer';
import { FetchLicensePackage } from '@account-users/account/state/licenses/licenses.actions';
import * as fromPermissions from '@account-users/user/state';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { FormState } from '@shared';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-create-account-modal',
  templateUrl: './create-account-modal.component.html',
  styleUrls: ['./create-account-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAccountModalComponent implements OnInit, OnDestroy {
  public readonly FormState = FormState;
  public readonly formState$ = this.store.select(
    fromAccount.getCreateAccountState,
  );
  public readonly accountStatus$ = this.store.select(
    fromAccount.getAccountStatus,
  );
  public readonly isBootstrapComplete$ = this.store.select(
    fromAccount.getIsBootstrapComplete,
  );
  public readonly isLoadingPackages$ = this.store.select(
    fromLicenses.getIsLoadingPackages,
  );
  public readonly licensePackagesError$ = this.store.select(
    fromLicenses.getLicensePackagesError,
  );

  public accountCategories$: Observable<AccountCategory[]>;
  public licensePackages$: Observable<LicensePackage[]>;
  public userPermissions$: Observable<Permission[]>;

  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _pendingAccountId$ = new BehaviorSubject<string | null>(
    null,
  );

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly router: Router,
    private readonly store: Store<State>,
  ) {
    this.userPermissions$ = this.store.select(
      fromPermissions.getUserPermissions,
    );

    this.licensePackages$ = combineLatest([
      this.store.select(fromLicenses.getLicensePackages),
      this.store.select(fromPermissions.getUserPermissions),
    ]).pipe(
      map(([licensePackages]) => {
        return licensePackages;
      }),
    );

    this.accountCategories$ = combineLatest([
      this.store.select(fromAccount.getAccountCategories),
      this.store.select(fromPermissions.getUserPermissions),
    ]).pipe(
      map(([accountCategories, userPermissions]) => {
        return accountCategories?.filter((category) => {
          switch (category.code) {
            case AccountCategoryCode.SuiteMaxo:
              return userPermissions?.includes(
                Permission.WawBackOfficePreLaunch,
              );
            default:
              return true;
          }
        });
      }),
    );
  }

  ngOnInit() {
    this.store
      .pipe(select(fromLicenses.getPackagesIsLoaded), takeUntil(this._destroy$))
      .subscribe((loaded: boolean) => {
        if (!loaded) {
          this.store.dispatch(new FetchLicensePackage());
        }
      });

    combineLatest([this.accountStatus$, this._pendingAccountId$])
      .pipe(
        filter(
          ([accountStatus, pendingAccountId]) =>
            accountStatus.audience === AccountState.Success &&
            accountStatus.adminInvite === AccountState.Success &&
            !!pendingAccountId,
        ),
      )
      .subscribe(([, pendingAccountId]) => {
        this.router.navigate([`/au/accounts/${pendingAccountId}`]);
        this.activeModal.close();
      });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onSubmit(account: AccountCreateRequest) {
    const { id } = account;
    this._pendingAccountId$.next(id);
    this.store.dispatch(new PrepareCreateAccount(account));
  }
}
